<template>
  <div class="ic-card">
    <!-- Photo -->
    <div class="ic-photo-link" style="cursor:pointer" @click="$emit('avatar-click')">
      <div class="ic-photo">
        <img v-show="imageLoaded" ref="avatar" @load="imageLoaded = true" :src="influencer.user.avatar_url" />
        <div v-show="!imageLoaded" class="ic-photo-skeleton"></div>

        <!-- Top-right overlays -->
        <div class="ic-top-actions" @click.stop>
          <note-influencer
            v-if="isBusiness && showNote"
            :influencer="influencer"
          />
          <favorite-influencer
            v-if="isBusiness"
            :influencer="influencer"
            ref="favoriteComponent"
          />
          <button v-if="isBusiness" class="ic-icon-btn" @click.prevent="startChat()">
            <i class="fal fa-comment"></i>
          </button>
          <button v-if="isModerator && connection_id" class="ic-icon-btn ic-icon-btn--danger" @click.prevent="deleteInfluencer()">
            <i class="fal fa-times"></i>
          </button>
        </div>

        <!-- New badge -->
        <div v-if="isNew" class="ic-badge-new">NEW</div>
      </div>
    </div>

    <!-- Info -->
    <div class="ic-info">
      <expired-timer v-if="timer" template="list" :campaignInfluencer="timer" />

      <!-- Row 1: country · name · age -->
      <div class="ic-name-row">
        <span class="ic-country">{{ influencer.country.name }}</span>
        <span class="ic-name">{{ influencer.user.first_name }} {{ influencer.user.last_name }}</span>
        <span class="ic-age">{{ influencer.gender === 'MALE' ? '♂' : '♀' }} {{ influencer.birthday | date_to_age }}</span>
      </div>

      <!-- Row 2: IG · TikTok · engagement (all inline) -->
      <div class="ic-ch-row">
        <span v-if="groupedChannelsData.instagram" class="ic-ch-item">
          <i class="fab fa-instagram"></i>
          {{ groupedChannelsData.instagram.followers | local_numbers }}
        </span>
        <span v-if="groupedChannelsData.tiktok" class="ic-ch-item">
          <i class="fab fa-tiktok"></i>
          {{ groupedChannelsData.tiktok.followers | local_numbers }}
        </span>
        <v-tooltip top v-if="engagementRate">
          <template v-slot:activator="{ on }">
            <span class="ic-ch-item ic-ch-item--eng" v-on="on">
              <i class="fal fa-heart"></i>
              {{ engagementRate }}
            </span>
          </template>
          <span>Engagement rate: avg. interactions per post ÷ followers</span>
        </v-tooltip>
      </div>
    </div>

    <!-- Footer slot -->
    <div v-if="$slots.footer" class="ic-footer">
      <slot name="footer"></slot>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapState } from "vuex";
import local_numbers from "@/helpers/filters/local_numbers";
import date_to_age from "@/helpers/filters/date_to_age";
import first_letter from "@/helpers/filters/first_letter";
import FavoriteInfluencer from "../inputs/FavoriteInfluencer";
import NoteInfluencer from "../inputs/NoteInfluencer";
import ExpiredTimer from "@/components/common/other/ExpiredTimer";
import { groupBy, mapValues, sumBy, get } from "lodash";

const getGroupedChannelsData = (channels) => {
  const channelGroups = groupBy(channels, "channel.name");
  return mapValues(channelGroups, (channels) => ({
    name: get(channels, [0, "channel", "name"]),
    icon: get(channels, [0, "channel", "icon"]),
    followers: sumBy(channels, "followers"),
  }));
};

export default {
  components: { FavoriteInfluencer, NoteInfluencer, ExpiredTimer },
  props: {
    influencer: { required: true },
    connection_id: { required: false },
    flat: { required: false, type: Boolean, default: false },
    timer: { type: Object, required: false, default: null },
    campaign_id: { type: Number, required: false, default: null },
    // Only lists that query "business_note" should show the note button
    showNote: { type: Boolean, required: false, default: false },
  },
  data() {
    return { isNew: null, imageLoaded: false };
  },
  filters: { date_to_age, local_numbers, first_letter },
  computed: {
    ...mapGetters("core/auth", ["isBusiness", "isModerator"]),
    ...mapState("core/auth", ["user"]),
    groupedChannelsData() {
      return getGroupedChannelsData(this.influencer.channels);
    },
    engagementRate() {
      return this.engagementRateCalc(this.influencer);
    },
  },
  methods: {
    ...mapActions("core/chats", ["createConversation", "toggleChatDialog", "setActiveConversation"]),
    updateFavorite(value) {
      this.influencer.is_favorite = value;
      this.$refs.favoriteComponent.updateState(value);
    },
    updateNew() {
      const today = Date.parse(new Date());
      const created_at = Date.parse(this.influencer.created_at);
      this.isNew = today - created_at < 2629746000;
    },
    deleteInfluencer() {
      this.$emit("delete", this.connection_id);
    },
    engagementRateCalc(influencer) {
      const ch = influencer.channels && influencer.channels[0];
      if (!ch || !ch.followers || ch.followers === 0) return null;
      if (!ch.media_count || ch.media_count === 0) return null;
      const totalInteractions = ch.total_likes > 0 ? ch.total_likes : ch.impressions;
      if (!totalInteractions || totalInteractions === 0) return null;
      const avgPerPost = totalInteractions / ch.media_count;
      return (avgPerPost / ch.followers * 100).toFixed(1) + '%';
    },
    startChat() {
      this.createConversation({
        type: "campaign_creator",
        campaign_id: this.campaign_id ? parseInt(this.campaign_id) : null,
        company_id: parseInt(this.user?.id),
        creator_id: parseInt(this.influencer.user.id),
      }).then((conversation) => {
        this.setActiveConversation(conversation.id);
        this.toggleChatDialog(true);
      });
    },
  },
  mounted() {
    this.updateNew();
  },
};
</script>

<style lang="scss" scoped>
/* ── Card shell ── */
.ic-card {
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  border: 1px solid var(--line, #eaecf0);
  background: var(--surface, #fff);
  overflow: hidden;
  transition: box-shadow 0.18s, transform 0.18s;

  &:hover {
    box-shadow: var(--shadow-md, 0 4px 16px rgba(0,0,0,0.10));
    transform: translateY(-2px);
  }
}

/* ── Photo ── */
.ic-photo-link {
  display: block;
  text-decoration: none;
}

.ic-photo {
  position: relative;
  width: 100%;
  padding-top: 115%; /* tall portrait */
  background: var(--surface-2, #f0f0f0);

  img,
  .ic-photo-skeleton {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .ic-photo-skeleton {
    background: linear-gradient(110deg, #e8e8e8 30%, #f5f5f5 50%, #e8e8e8 70%);
    background-size: 200% 100%;
    animation: ic-shimmer 1.4s infinite;
  }
}

@keyframes ic-shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* ── Overlay action buttons (top-right) ── */
.ic-top-actions {
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
}

.ic-icon-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: #ffffff;
  box-shadow: 0 1px 5px rgba(0, 0, 0, 0.14);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 13px;
  color: var(--ink-2, #555);
  transition: background 0.15s, color 0.15s;

  &:hover { background: #fff; color: var(--accent, #e05540); }
  &--danger:hover { color: #d93025; }
}

::v-deep .favorite-btn,
::v-deep .ic-fav-btn {
  width: 32px !important;
  height: 32px !important;
  border-radius: 8px !important;
  background: #ffffff !important;
  box-shadow: 0 1px 5px rgba(0, 0, 0, 0.14) !important;
  border: none !important;
}

/* ── New badge ── */
.ic-badge-new {
  position: absolute;
  top: 8px;
  left: 8px;
  background: var(--accent, #e05540);
  color: #fff;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.06em;
  padding: 2px 7px;
  border-radius: 999px;
}

/* ── Info block ── */
.ic-info {
  padding: 10px 12px 6px;
  display: flex;
  flex-direction: column;
  gap: 5px;
  flex: 1;
}

/* Row 1: country · name · age */
.ic-name-row {
  display: flex;
  align-items: baseline;
  gap: 4px;
  min-width: 0;
}

.ic-country {
  font-size: 11px;
  color: var(--ink-3, #aaa);
  white-space: nowrap;
  flex-shrink: 0;
}

.ic-name {
  font-size: 13px;
  font-weight: 700;
  color: var(--ink-1, #111);
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ic-age {
  font-size: 11px;
  color: var(--ink-3, #aaa);
  white-space: nowrap;
  flex-shrink: 0;
}

/* Row 2: channels inline */
.ic-ch-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.ic-ch-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: var(--ink-2, #555);

  i {
    font-size: 12px;
    color: var(--ink-3, #aaa);
  }

  &--eng i { color: #e05540; }
}

.ic-ch-item--eng {
  margin-left: auto;
}

.ic-muted {
  color: var(--ink-3, #aaa);
  font-size: 11px;
}

.ic-ch-item .fa-instagram {
  color: #e05540;
}

.ic-ch-item .fa-tiktok {
  color: #000;
}

/* ── Footer slot ── */
.ic-footer {
  padding: 8px 10px 10px;
  display: flex;
  flex-direction: row;   /* ← side by side */
  gap: 6px;
}
</style>